import { getBackendUrl } from "@/lib/backend";
import { formatApiError } from "@/lib/apiError";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { CalendarDays, Clock, Sparkles } from "lucide-react";

const API = `${getBackendUrl()}/api`;

const SLOTS = [
  "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM",
  "4:00 PM", "4:30 PM", "5:00 PM", "5:30 PM",
];

function formatDate(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function DemoBookingDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: date/slot, 2: details, 3: done
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", company: "", notes: "" });
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setStep(1);
    setDate(null);
    setSlot("");
    setForm({ name: "", email: "", company: "", notes: "" });
    setLoading(false);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/demo-booking`, {
        ...form,
        date: formatDate(date),
        time_slot: slot,
      });
      toast.success("Walkthrough booked. We'll send a confirmation soon.");
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err, "Could not book. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) setTimeout(reset, 300);
  };

  // Disable past dates
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid="demo-booking-dialog"
        className="bg-[#0E0E0E] border border-white/10 text-white max-w-3xl rounded-2xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)]"
      >
        <DialogHeader>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
            <Sparkles className="w-3 h-3" /> Studio Walkthrough · 15 min
          </div>
          <DialogTitle className="font-display text-2xl md:text-3xl tracking-tight text-white mt-1">
            {step === 3 ? "You're booked." : "Book a private demo"}
          </DialogTitle>
          <DialogDescription className="text-white/55 text-sm">
            {step === 1 && "Pick a date and time. Our team will guide you through Facetra Studio live."}
            {step === 2 && "Just a few details and you're set."}
            {step === 3 && "We'll send a confirmation and a link to join shortly."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-3 flex items-center gap-2">
                <CalendarDays className="w-3.5 h-3.5" /> Choose a date
              </div>
              <div className="rounded-xl border border-white/10 bg-[#141414] p-2">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < today}
                  initialFocus
                  className="text-white"
                  data-testid="booking-calendar"
                />
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-3 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> Pick a slot · IST
              </div>
              <div className="grid grid-cols-3 gap-2 max-h-[280px] overflow-y-auto pr-1">
                {SLOTS.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSlot(s)}
                    disabled={!date}
                    data-testid={`booking-slot-${s.replace(/[^a-z0-9]/gi, "")}`}
                    className={`text-xs py-2.5 rounded-lg border transition-all ${
                      slot === s
                        ? "bg-[#D4AF37]/15 border-[#D4AF37]/60 text-[#D4AF37]"
                        : "bg-[#141414] border-white/10 text-white/70 hover:border-white/25"
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep(2)}
                disabled={!date || !slot}
                data-testid="booking-continue-btn"
                className="w-full mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3 hover:bg-[#F3CE5A] transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 mt-2">
            <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#141414] px-4 py-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Slot</div>
                <div className="text-sm text-white mt-0.5">
                  {date?.toLocaleDateString("en-IN", { weekday: "long", month: "short", day: "numeric" })} · {slot}
                </div>
              </div>
              <button
                onClick={() => setStep(1)}
                className="text-xs text-[#D4AF37] hover:text-[#F3CE5A]"
              >
                Change
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="booking-input-name"
                placeholder="Full name"
                className="bg-[#141414] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="booking-input-email"
                placeholder="Email"
                className="bg-[#141414] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              />
            </div>
            <input
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              data-testid="booking-input-company"
              placeholder="Company / Atelier (optional)"
              className="w-full bg-[#141414] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
            />
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="booking-input-notes"
              rows={3}
              placeholder="Anything specific you'd like to see? (optional)"
              className="w-full bg-[#141414] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition resize-none"
            />
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setStep(1)}
                className="text-sm px-5 py-3 rounded-full border border-white/15 text-white/75 hover:bg-white/5 transition"
              >
                ← Back
              </button>
              <button
                onClick={submit}
                disabled={loading}
                data-testid="booking-confirm-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3 hover:bg-[#F3CE5A] transition disabled:opacity-60"
              >
                {loading ? "Booking…" : "Confirm walkthrough →"}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="text-center py-6">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div className="mt-5 text-white text-base">
              {date?.toLocaleDateString("en-IN", { weekday: "long", month: "long", day: "numeric" })}
              {" · "}
              <span className="text-[#D4AF37]">{slot}</span>
            </div>
            <p className="mt-2 text-sm text-white/55 max-w-sm mx-auto">
              We've reserved this slot for {form.name}. A confirmation will land in {form.email} shortly.
            </p>
            <button
              onClick={() => handleOpenChange(false)}
              data-testid="booking-done-btn"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium px-6 py-2.5 hover:bg-[#F3CE5A] transition"
            >
              Done
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
